import * as Yup from "yup";
import { resolvePlantLoanPlan } from "../../../../../infrastructure/utils/plantLoanPlans";

export const MIN_INSTALLABLE_AREA_PER_KW = 8;

export const getMinInstallableAreaForCapacity = (plantCapacity) => {
  const kw = Number(plantCapacity);
  if (!kw || Number.isNaN(kw)) return null;
  return kw * MIN_INSTALLABLE_AREA_PER_KW;
};

export const getMinAreaValidationMessage = (plantCapacity) => {
  const min = getMinInstallableAreaForCapacity(plantCapacity);
  if (!min) return "مساحت فضای قابل نصب برای ظرفیت انتخاب‌شده کافی نیست.";
  return `برای نیروگاه ${Number(plantCapacity)} کیلوواتی، حداقل ${min} متر مربع فضای قابل نصب لازم است.`;
};

export const INITIAL_CONDITIONS_ERROR_MESSAGE =
  "برای ادامه، مطالعه و تأیید شرایط احداث الزامی است.";

export const isApartmentInstallation = (installationType) =>
  installationType === "Apartment" || installationType === 2 || installationType === "2";

const hasUploadedUrl = (value) =>
  typeof value === "string" && value.trim().length > 0;

const apartmentDocumentField = (requiredMessage) =>
  Yup.mixed().test("apartment-document", requiredMessage, function validateApartmentDocument(value) {
    if (!isApartmentInstallation(this.parent.installationType)) {
      return true;
    }
    if (hasUploadedUrl(value)) {
      return true;
    }
    return this.createError({ message: requiredMessage });
  });

export const step0ValidationSchema = Yup.object().shape({
  initialConditionsAccepted: Yup.boolean()
    .oneOf([true], INITIAL_CONDITIONS_ERROR_MESSAGE)
    .required(INITIAL_CONDITIONS_ERROR_MESSAGE),
});

export const step1ValidationSchema = Yup.object().shape({
  powerPlantType: Yup.string()
    .required("لطفا نوع نیروگاه را انتخاب کنید.")
    .test(
      "loan-power-plant-type",
      "برای دریافت وام، نوع نیروگاه باید متصل به شبکه، هیبریدی یا هیبریدی بدون باطری باشد.",
      function validateLoanPowerPlantType(val) {
        if (!this.parent.needsLoan) return true;
        return resolvePlantLoanPlan(val).available;
      }
    ),
  locationUsageType: Yup.string().required("لطفا کاربری محل نصب را انتخاب کنید."),
  plantCapacity: Yup.string().required("لطفا این فیلد را پر کنید."),
  installableAreaSquareMeter: Yup.number()
    .typeError("مساحت فضای قابل نصب باید عدد باشد.")
    .min(1, "مساحت باید بزرگتر از صفر باشد.")
    .required("لطفا مساحت فضای قابل نصب را وارد کنید.")
    .test("min-area-for-capacity", function (value) {
      const { plantCapacity } = this.parent;
      const minArea = getMinInstallableAreaForCapacity(plantCapacity);
      if (minArea == null || value == null || value === "") return true;
      if (Number(value) >= minArea) return true;
      return this.createError({
        message: getMinAreaValidationMessage(plantCapacity),
      });
    }),
  electricBoillNumber: Yup.string()
    .required("شناسه قبض برق الزامی است")
    .matches(/^\d{13}$/, "شناسه قبض باید دقیقاً ۱۳ رقم باشد"),
  installationType: Yup.string().required("لطفا نوع محل نصب را انتخاب کنید."),
  unitCount: Yup.mixed().test("unit-count-apartment", function validateUnitCount(value) {
    if (!isApartmentInstallation(this.parent.installationType)) {
      return true;
    }
    if (value === "" || value === null || value === undefined) {
      return this.createError({ message: "تعداد واحدها الزامی است." });
    }
    const num = Number(value);
    if (Number.isNaN(num)) {
      return this.createError({ message: "تعداد واحدها باید عدد باشد." });
    }
    if (num < 1) {
      return this.createError({ message: "تعداد واحدها باید حداقل 1 باشد." });
    }
    return true;
  }),
  needsLoan: Yup.boolean()
    .required("لطفا نیاز به وام را مشخص کنید.")
    .test(
      "loan-plan-available",
      "برای نیروگاه آفگرید در حال حاضر وام فعال نیست.",
      function validateLoanAvailability(value) {
        if (!value) return true;
        const { powerPlantType } = this.parent;
        if (!powerPlantType) return true;
        return resolvePlantLoanPlan(powerPlantType).available;
      }
    ),
  loanGuaranteeChequeIntent: Yup.mixed().when("needsLoan", {
    is: true,
    then: () =>
      Yup.string()
        .required("لطفاً وضعیت چک صیادی را مشخص کنید.")
        .oneOf(["1", "2", "3"], "گزینه وضعیت چک صیادی نامعتبر است."),
    otherwise: () => Yup.mixed().nullable(),
  }),
  guarantorFirstName: Yup.string().when(
    ["needsLoan", "loanGuaranteeChequeIntent"],
    {
      is: (needsLoan, intent) => needsLoan && String(intent) === "2",
      then: () => Yup.string().trim().required("نام ضامن الزامی است."),
      otherwise: () => Yup.string().nullable(),
    }
  ),
  guarantorLastName: Yup.string().when(
    ["needsLoan", "loanGuaranteeChequeIntent"],
    {
      is: (needsLoan, intent) => needsLoan && String(intent) === "2",
      then: () => Yup.string().trim().required("نام خانوادگی ضامن الزامی است."),
      otherwise: () => Yup.string().nullable(),
    }
  ),
  guarantorNationalCode: Yup.string().when(
    ["needsLoan", "loanGuaranteeChequeIntent"],
    {
      is: (needsLoan, intent) => needsLoan && String(intent) === "2",
      then: () =>
        Yup.string()
          .required("کد ملی ضامن الزامی است.")
          .matches(/^\d{10}$/, "کد ملی ضامن باید ۱۰ رقم باشد."),
      otherwise: () => Yup.string().nullable(),
    }
  ),
  guarantorMobile: Yup.string().when(
    ["needsLoan", "loanGuaranteeChequeIntent"],
    {
      is: (needsLoan, intent) => needsLoan && String(intent) === "2",
      then: () =>
        Yup.string()
          .required("موبایل ضامن الزامی است.")
          .matches(/^09\d{9}$/, "موبایل ضامن باید ۱۱ رقم و با ۰۹ شروع شود."),
      otherwise: () => Yup.string().nullable(),
    }
  ),
  ownershipStatus: Yup.string().required("لطفا وضعیت مالکیت را انتخاب کنید."),
  platformAcquaintanceSource: Yup.string().required("لطفا نحوه آشنایی را انتخاب کنید."),
  applicantGoalFlags: Yup.mixed().test(
    "applicant-goals",
    "حداقل یک هدف متقاضی را انتخاب کنید.",
    (value) => (Number(value) || 0) > 0
  ),
});

export const step3ValidationSchema = Yup.object().shape({
  xPoint: Yup.number().required("لطفا موقعیت را از روی نقشه یا لینک انتخاب کنید."),
  yPoint: Yup.number().required("لطفا موقعیت را از روی نقشه یا لینک انتخاب کنید."),
  provinceId: Yup.string().required("لطفا استان را انتخاب کنید."),
  cityId: Yup.string().required("لطفا شهرستان را انتخاب کنید."),
  address: Yup.string().required("لطفا این فیلد را پر کنید."),
  postalCode: Yup.string()
    .required("لطفا کد پستی را وارد کنید.")
    .matches(/^[0-9]{10}$/, "کد پستی باید دقیقاً 10 رقم باشد."),
  boardConsentDocumentUrl: apartmentDocumentField(
    "بارگذاری رضایت‌نامه هیئت‌مدیره الزامی است."
  ),
  authorizedRepresentativeLetterUrl: apartmentDocumentField(
    "بارگذاری نامه معرفی نماینده الزامی است."
  ),
  agreementDocumentUrl: apartmentDocumentField(
    "بارگذاری تفاهم‌نامه همکاری الزامی است."
  ),
});

export const ELECTRIC_BILL_FILE_ACCEPT = "image/*,application/pdf";

export const step4ValidationSchema = Yup.object().shape({
  electricBoillUrl: Yup.string().required(
    "لطفا تصویر یا فایل PDF قبض برق را بارگذاری کنید."
  ),
  plantInstallImage1Url: Yup.string().required(
    "بارگذاری عکس اول محل نصب الزامی است."
  ),
  plantInstallImage2Url: Yup.string().required(
    "بارگذاری عکس دوم محل نصب الزامی است."
  ),
  nationalCardUrl: Yup.string().required("تصویر روی کارت ملی الزامی است."),
  nationalCardBackUrl: Yup.string().required("تصویر پشت کارت ملی الزامی است."),
  boardConsentDocumentUrl: apartmentDocumentField(
    "برای درخواست آپارتمانی، بارگذاری رضایت‌نامه هیئت‌مدیره اجباری است."
  ),
  authorizedRepresentativeLetterUrl: apartmentDocumentField(
    "برای درخواست آپارتمانی، بارگذاری نامه معرفی نماینده اجباری است."
  ),
  agreementDocumentUrl: apartmentDocumentField(
    "برای درخواست آپارتمانی، بارگذاری تفاهم‌نامه همکاری اجباری است."
  ),
});

export const assistedProfileValidationSchema = Yup.object().shape({
  firstName: Yup.string().required("نام الزامی است."),
  lastName: Yup.string().required("نام خانوادگی الزامی است."),
  fatherName: Yup.string().required("نام پدر الزامی است."),
  birthDate: Yup.mixed()
    .test(
      "birthdate-valid",
      "تاریخ تولد الزامی است.",
      (value) => {
        if (!value) return false;
        const d = value instanceof Date ? value : new Date(value);
        return !Number.isNaN(d.getTime());
      }
    )
    .required("تاریخ تولد الزامی است."),
  nationalCode: Yup.string()
    .required("کد ملی الزامی است.")
    .matches(/^\d{10}$/, "کد ملی باید ۱۰ رقم باشد."),
  telephon: Yup.string()
    .matches(/^[0-9+\-\s()]+$/, "شماره تلفن معتبر نیست.")
    .min(10, "شماره تلفن باید حداقل ۱۰ رقم باشد.")
    .required("شماره تلفن ثابت الزامی است."),
  email: Yup.string().email("ایمیل معتبر نیست.").nullable(),
});

export const getAssistedRequestStepSchema = (requestStep) => {
  switch (requestStep) {
    case 0:
      return step0ValidationSchema;
    case 1:
      return step1ValidationSchema;
    case 2:
      return step3ValidationSchema;
    case 3:
      return step4ValidationSchema;
    default:
      return step0ValidationSchema
        .concat(step1ValidationSchema)
        .concat(step3ValidationSchema)
        .concat(step4ValidationSchema);
  }
};
